import { array, boolean, enum as enumFactory, iso, literal, null as nullFactory, number, object, preprocess, strictObject, string, union, unknown } from "../../lazy-zod.js";
import { agentProfileIdSchema, openMeldRuntimeProfileIdSchema, profileWorkingDirectorySchema, uuidProfileIdSchema } from "../protocol/profile.js";
import { spaceMentionLabelSchema, spaceMentionLabelSourceSchema } from "./space-mention-label.js";
import { wakeDeviceWaitTargetSchema } from "../protocol/wake-device-wait.js";
import { agentControllerPermissionModeSchema } from "../agent-controller-permission-mode.js";
import { agentProfileBindingModelSelectionSchema } from "./agent-profile-binding.js";
import { agentExecutionReadinessSchema, agentWakeAvailabilitySchema, agentWakeRouteRegistrationSchema, wakeRouteEvidenceSchema, wakeRouteStatusSchema } from "./agent-execution-readiness.js";
//#region src/schemas/http/space-members.ts
const ISO_DATE_TIME_SCHEMA = iso.datetime({ offset: true });
const MAX_SPACE_MEMBERS_BATCH_SIZE = 100;
const trimmedNonEmptyStringSchema = string().trim().min(1);
const profileIdSchema = openMeldRuntimeProfileIdSchema;
const nullableTrimmedStringSchema = union([trimmedNonEmptyStringSchema, nullFactory()]);
const nullableRuntimeProfileIdSchema = union([profileIdSchema, nullFactory()]);
const nullableIsoDateTimeSchema = union([ISO_DATE_TIME_SCHEMA, nullFactory()]);
const retiredAgentSnapshotKeys = /* @__PURE__ */ new Set([
	"agentExecutionReadiness",
	"daemonRouteObservation",
	"publicReplyPresence",
	"publicPresence",
	"viewerLocalControl",
	"viewerLocalRecovery",
	"wakeState",
	"wakeability"
]);
const rejectRetiredAgentSnapshotKeysSchema = unknown().superRefine((value, ctx) => {
	if (!(value && typeof value === "object")) return;
	for (const key of retiredAgentSnapshotKeys) {
		if (!Object.hasOwn(value, key)) continue;
		ctx.addIssue({
			code: "custom",
			message: `retired agent member field is not allowed: ${key}`,
			path: [key]
		});
	}
});
function validateRuntimeProfileIdForMemberKind(input) {
	if ((input.profileKind === "human" ? uuidProfileIdSchema : agentProfileIdSchema).safeParse(input.profileId).success) return;
	input.ctx.addIssue({
		code: "custom",
		message: input.profileKind === "human" ? "human profileId must be a UUID" : "agent profileId must use agt_<UUID>",
		path: input.path
	});
}
const spaceMemberKindSchema = enumFactory(["human", "agent"]);
const spaceMemberStatusSchema = enumFactory(["active", "removed"]);
const spaceMemberSourceSchema = enumFactory([
	"space_creator",
	"joined_directly",
	"added_by_member",
	"backfilled"
]);
const spaceMemberAgentExecutionReadinessSchema = agentExecutionReadinessSchema;
const spaceMemberAgentWakeAvailabilitySchema = agentWakeAvailabilitySchema;
const spaceMemberAgentOwnerWakeDiagnosticsSchema = object({
	state: enumFactory([
		"none",
		"checking_wake_status",
		"action_required"
	]),
	agentExecutionReadiness: spaceMemberAgentExecutionReadinessSchema,
	agentWakeRouteRegistration: agentWakeRouteRegistrationSchema,
	wakeRouteEvidence: wakeRouteEvidenceSchema,
	wakeRouteStatus: wakeRouteStatusSchema
});
const baseSpaceMemberSnapshotSchema = object({
	profileId: profileIdSchema,
	profileName: trimmedNonEmptyStringSchema,
	profileKind: spaceMemberKindSchema,
	status: spaceMemberStatusSchema,
	memberSource: spaceMemberSourceSchema,
	memberSourceLabel: trimmedNonEmptyStringSchema,
	addedAt: ISO_DATE_TIME_SCHEMA,
	removedAt: nullableIsoDateTimeSchema,
	createdAt: ISO_DATE_TIME_SCHEMA,
	updatedAt: ISO_DATE_TIME_SCHEMA,
	addedByProfileId: nullableRuntimeProfileIdSchema,
	addedByOwnerUserId: nullableTrimmedStringSchema,
	addedByDisplayName: nullableTrimmedStringSchema,
	removedByProfileId: nullableRuntimeProfileIdSchema,
	removedByOwnerUserId: nullableTrimmedStringSchema,
	removalReason: nullableTrimmedStringSchema,
	firstEnteredAt: nullableIsoDateTimeSchema,
	lastEnteredAt: nullableIsoDateTimeSchema,
	lastSeenAt: nullableIsoDateTimeSchema,
	lastSeenLabel: nullableTrimmedStringSchema,
	liveNow: boolean(),
	avatarUrl: nullableTrimmedStringSchema.optional()
});
const spaceMemberHumanSnapshotSchema = baseSpaceMemberSnapshotSchema.extend({
	profileKind: literal("human"),
	mentionAlias: nullableTrimmedStringSchema,
	ownerUserId: nullableTrimmedStringSchema,
	ownerName: nullableTrimmedStringSchema
}).superRefine((value, ctx) => {
	validateRuntimeProfileIdForMemberKind({
		profileId: value.profileId,
		profileKind: "human",
		ctx,
		path: ["profileId"]
	});
});
const spaceMemberAgentSnapshotSchema = rejectRetiredAgentSnapshotKeysSchema.pipe(baseSpaceMemberSnapshotSchema.extend({
	profileKind: literal("agent"),
	mentionAlias: nullableTrimmedStringSchema,
	ownerUserId: nullableTrimmedStringSchema,
	ownerName: nullableTrimmedStringSchema,
	agentControllerRef: nullableTrimmedStringSchema,
	agentControllerPermissionMode: agentControllerPermissionModeSchema.optional(),
	runtimeDeviceDisplayName: nullableTrimmedStringSchema.optional(),
	deviceWaitTarget: wakeDeviceWaitTargetSchema.optional(),
	agentControllerConversationId: nullableTrimmedStringSchema,
	modelSelection: agentProfileBindingModelSelectionSchema.optional(),
	workingDirectory: profileWorkingDirectorySchema.nullable().optional(),
	agentWakeAvailability: spaceMemberAgentWakeAvailabilitySchema.optional(),
	ownerWakeDiagnostics: union([spaceMemberAgentOwnerWakeDiagnosticsSchema, nullFactory()]).optional()
}).superRefine((value, ctx) => {
	validateRuntimeProfileIdForMemberKind({
		profileId: value.profileId,
		profileKind: "agent",
		ctx,
		path: ["profileId"]
	});
}));
const spaceMembersSummarySchema = object({
	totalCount: number().int().min(0),
	humanCount: number().int().min(0),
	agentCount: number().int().min(0),
	liveNowCount: number().int().min(0),
	wakeableAgentCount: number().int().min(0).optional(),
	checkingAgentCount: number().int().min(0).optional(),
	offlineAgentCount: number().int().min(0).optional(),
	unavailableAgentCount: number().int().min(0).optional()
});
const spaceMembersSnapshotSchema = object({
	spaceId: trimmedNonEmptyStringSchema,
	generatedAt: ISO_DATE_TIME_SCHEMA,
	summary: spaceMembersSummarySchema,
	humans: array(spaceMemberHumanSnapshotSchema),
	agents: array(spaceMemberAgentSnapshotSchema)
}).superRefine((value, ctx) => {
	const totalCount = value.humans.length + value.agents.length;
	const liveNowCount = [...value.humans, ...value.agents].filter((item) => item.liveNow).length;
	const wakeableAgentCount = value.agents.filter(isPublicWakeableAgent).length;
	const checkingAgentCount = value.agents.filter(isPublicCheckingAgent).length;
	const offlineAgentCount = value.agents.filter(isPublicOfflineAgent).length;
	const unavailableAgentCount = value.agents.filter(isPublicUnavailableAgent).length;
	addSummaryMismatchIssue({
		ctx,
		actual: totalCount,
		expected: value.summary.totalCount,
		path: ["summary", "totalCount"],
		label: "totalCount"
	});
	addSummaryMismatchIssue({
		ctx,
		actual: value.humans.length,
		expected: value.summary.humanCount,
		path: ["summary", "humanCount"],
		label: "humanCount"
	});
	addSummaryMismatchIssue({
		ctx,
		actual: value.agents.length,
		expected: value.summary.agentCount,
		path: ["summary", "agentCount"],
		label: "agentCount"
	});
	addSummaryMismatchIssue({
		ctx,
		actual: liveNowCount,
		expected: value.summary.liveNowCount,
		path: ["summary", "liveNowCount"],
		label: "liveNowCount"
	});
	if (value.summary.wakeableAgentCount !== void 0) addSummaryMismatchIssue({
		ctx,
		actual: wakeableAgentCount,
		expected: value.summary.wakeableAgentCount,
		path: ["summary", "wakeableAgentCount"],
		label: "wakeableAgentCount"
	});
	if (value.summary.checkingAgentCount !== void 0) addSummaryMismatchIssue({
		ctx,
		actual: checkingAgentCount,
		expected: value.summary.checkingAgentCount,
		path: ["summary", "checkingAgentCount"],
		label: "checkingAgentCount"
	});
	if (value.summary.offlineAgentCount !== void 0) addSummaryMismatchIssue({
		ctx,
		actual: offlineAgentCount,
		expected: value.summary.offlineAgentCount,
		path: ["summary", "offlineAgentCount"],
		label: "offlineAgentCount"
	});
	if (value.summary.unavailableAgentCount !== void 0) addSummaryMismatchIssue({
		ctx,
		actual: unavailableAgentCount,
		expected: value.summary.unavailableAgentCount,
		path: ["summary", "unavailableAgentCount"],
		label: "unavailableAgentCount"
	});
});
const spaceViewerMembershipProfileSchema = strictObject({
	profileId: profileIdSchema,
	profileKind: spaceMemberKindSchema,
	profileName: trimmedNonEmptyStringSchema,
	lastEnteredAt: nullableIsoDateTimeSchema
});
const spaceViewerMembershipResponseSchema = strictObject({
	spaceId: trimmedNonEmptyStringSchema,
	activeMemberProfiles: array(spaceViewerMembershipProfileSchema),
	preferredOpenProfileId: nullableRuntimeProfileIdSchema
});
const spaceIdentityDirectoryEntrySchema = strictObject({
	profileId: profileIdSchema,
	profileName: trimmedNonEmptyStringSchema,
	profileKind: spaceMemberKindSchema,
	mentionAlias: nullableTrimmedStringSchema,
	ownerUserId: nullableTrimmedStringSchema,
	ownerName: nullableTrimmedStringSchema,
	agentControllerRef: nullableTrimmedStringSchema
}).superRefine((value, ctx) => {
	validateRuntimeProfileIdForMemberKind({
		profileId: value.profileId,
		profileKind: value.profileKind,
		ctx,
		path: ["profileId"]
	});
});
const spaceIdentityDirectorySchema = strictObject({
	spaceId: trimmedNonEmptyStringSchema,
	generatedAt: ISO_DATE_TIME_SCHEMA,
	entries: array(spaceIdentityDirectoryEntrySchema)
});
const spaceMemberDirectoryAllowedRelationsSchema = preprocess((value) => {
	if (!(value && typeof value === "object") || Array.isArray(value)) return value;
	const { cc: _legacyCc, ...withoutLegacyCc } = value;
	if (typeof withoutLegacyCc.mention === "boolean") return withoutLegacyCc;
	if (typeof withoutLegacyCc.wake !== "boolean" || typeof withoutLegacyCc.reference !== "boolean") return withoutLegacyCc;
	const wasHumanReference = !withoutLegacyCc.wake && withoutLegacyCc.reference;
	return {
		...withoutLegacyCc,
		mention: wasHumanReference,
		reference: wasHumanReference ? false : withoutLegacyCc.reference
	};
}, strictObject({
	wake: boolean(),
	reference: boolean(),
	mention: boolean()
}));
const spaceMemberDirectoryCardSchema = strictObject({
	role: nullableTrimmedStringSchema,
	roleDescription: nullableTrimmedStringSchema
});
const spaceMemberDirectoryEntrySchema = strictObject({
	profileId: profileIdSchema,
	profileKind: spaceMemberKindSchema,
	displayName: trimmedNonEmptyStringSchema,
	primaryMentionLabel: spaceMentionLabelSchema,
	mentionLabels: array(spaceMentionLabelSchema).min(1),
	mentionAlias: nullableTrimmedStringSchema,
	labelSource: spaceMentionLabelSourceSchema,
	lookupTokens: array(trimmedNonEmptyStringSchema).min(1),
	preferredLookupToken: trimmedNonEmptyStringSchema,
	profileIdShort: trimmedNonEmptyStringSchema,
	allowedRelations: spaceMemberDirectoryAllowedRelationsSchema,
	ownerUserId: nullableTrimmedStringSchema,
	ownerName: nullableTrimmedStringSchema,
	agentCard: union([spaceMemberDirectoryCardSchema, nullFactory()]),
	spaceRole: union([spaceMemberDirectoryCardSchema, nullFactory()])
}).superRefine((value, ctx) => {
	validateRuntimeProfileIdForMemberKind({
		profileId: value.profileId,
		profileKind: value.profileKind,
		ctx,
		path: ["profileId"]
	});
	if (value.primaryMentionLabel && !value.mentionLabels.includes(value.primaryMentionLabel)) ctx.addIssue({
		code: "custom",
		message: "primaryMentionLabel must be included in mentionLabels",
		path: ["primaryMentionLabel"]
	});
	if (value.profileKind === "human" && value.allowedRelations.wake) ctx.addIssue({
		code: "custom",
		message: "human members cannot support Wake",
		path: ["allowedRelations", "wake"]
	});
	if (value.profileKind === "human" && value.allowedRelations.reference) ctx.addIssue({
		code: "custom",
		message: "human members cannot support Reference",
		path: ["allowedRelations", "reference"]
	});
	if (value.profileKind === "agent" && value.allowedRelations.mention) ctx.addIssue({
		code: "custom",
		message: "agent members cannot support Human Mention",
		path: ["allowedRelations", "mention"]
	});
});
const spaceMemberDirectorySchema = strictObject({
	spaceId: trimmedNonEmptyStringSchema,
	generatedAt: ISO_DATE_TIME_SCHEMA,
	members: array(spaceMemberDirectoryEntrySchema)
});
const postSpaceMembersBodySchema = strictObject({ memberProfileIds: array(profileIdSchema).min(1).max(MAX_SPACE_MEMBERS_BATCH_SIZE) });
const addCenterAgentToSpaceBodySchema = strictObject({ expectedCenterAgentProfileId: agentProfileIdSchema });
const addOrganizationDirectoryProfilesToSpaceBodySchema = strictObject({
	directoryProfileIds: array(profileIdSchema).min(1).max(MAX_SPACE_MEMBERS_BATCH_SIZE),
	expectedCenterAgentProfileId: agentProfileIdSchema.optional()
}).superRefine((value, ctx) => {
	if (new Set(value.directoryProfileIds).size !== value.directoryProfileIds.length) ctx.addIssue({
		code: "custom",
		message: "directoryProfileIds must not contain duplicates",
		path: ["directoryProfileIds"]
	});
	if (value.expectedCenterAgentProfileId && !value.directoryProfileIds.includes(value.expectedCenterAgentProfileId)) ctx.addIssue({
		code: "custom",
		message: "expectedCenterAgentProfileId must be included in directoryProfileIds",
		path: ["expectedCenterAgentProfileId"]
	});
});
const removeSpaceMembersBodySchema = strictObject({ memberProfileIds: array(profileIdSchema).min(1).max(MAX_SPACE_MEMBERS_BATCH_SIZE) });
const resolvedSpaceMemberProfileSchema = strictObject({
	profileId: profileIdSchema,
	profileName: trimmedNonEmptyStringSchema,
	profileKind: spaceMemberKindSchema,
	ownerUserId: nullableTrimmedStringSchema,
	ownerName: nullableTrimmedStringSchema,
	agentControllerRef: nullableTrimmedStringSchema
}).superRefine((value, ctx) => {
	validateRuntimeProfileIdForMemberKind({
		profileId: value.profileId,
		profileKind: value.profileKind,
		ctx,
		path: ["profileId"]
	});
});
const postResolvedSpaceMembersBodySchema = strictObject({ members: array(resolvedSpaceMemberProfileSchema).min(1).max(MAX_SPACE_MEMBERS_BATCH_SIZE) });
const postSpaceMembersResponseSchema = strictObject({
	addedMemberIds: array(profileIdSchema),
	alreadyMemberIds: array(profileIdSchema),
	items: array(union([spaceMemberHumanSnapshotSchema, spaceMemberAgentSnapshotSchema]))
});
const removeSpaceMembersResponseSchema = strictObject({
	removedMemberIds: array(profileIdSchema),
	alreadyAbsentMemberIds: array(profileIdSchema)
});
const leaveSpaceResponseSchema = strictObject({
	leftProfileId: profileIdSchema,
	status: enumFactory(["left", "already_absent"])
});
function addSummaryMismatchIssue(input) {
	if (input.actual === input.expected) return;
	input.ctx.addIssue({
		code: "custom",
		message: `${input.label} must match derived member counts`,
		path: input.path
	});
}
function isPublicWakeableAgent(agent) {
	return agent.agentWakeAvailability?.status === "available_for_wake";
}
function isPublicCheckingAgent(agent) {
	return agent.agentWakeAvailability?.status === "checking_connection";
}
function isPublicOfflineAgent(agent) {
	return agent.agentWakeAvailability?.status === "offline_for_wake";
}
function isPublicUnavailableAgent(agent) {
	return agent.agentWakeAvailability?.status === "unavailable_for_wake";
}
//#endregion
export { addCenterAgentToSpaceBodySchema, addOrganizationDirectoryProfilesToSpaceBodySchema, leaveSpaceResponseSchema, postResolvedSpaceMembersBodySchema, postSpaceMembersBodySchema, postSpaceMembersResponseSchema, removeSpaceMembersBodySchema, removeSpaceMembersResponseSchema, resolvedSpaceMemberProfileSchema, spaceIdentityDirectoryEntrySchema, spaceIdentityDirectorySchema, spaceMemberAgentExecutionReadinessSchema, spaceMemberAgentOwnerWakeDiagnosticsSchema, spaceMemberAgentSnapshotSchema, spaceMemberAgentWakeAvailabilitySchema, spaceMemberDirectoryAllowedRelationsSchema, spaceMemberDirectoryCardSchema, spaceMemberDirectoryEntrySchema, spaceMemberDirectorySchema, spaceMemberHumanSnapshotSchema, spaceMemberKindSchema, spaceMemberSourceSchema, spaceMemberStatusSchema, spaceMembersSnapshotSchema, spaceMembersSummarySchema, spaceViewerMembershipProfileSchema, spaceViewerMembershipResponseSchema };

//# sourceMappingURL=space-members.js.map